import React from 'react';
import { Dialog, Slide } from '@mui/material';
import AddDataSourceFormContainer from '../AddDataSourceFormContainer';
import { TransitionProps } from '@mui/material/transitions';
import flowTypes from 'new/components/flows/types/flowTypes';
import CreateReplicationFlow from 'new/components/flows/types/replication/CreateReplicationFlow';
import CreateSparkFlow from 'new/components/flows/types/spark/CreateSparkFlow';
import CreateELTFlow from 'new/components/flows/types/elt/CreateELTFlow';
import CreateDirectFlow from 'new/components/flows/types/DirectFlow/CreateDirectFlow';
import CreateCDCFlow from 'new/components/flows/types/cdc/CreateCDCFlow/CreateFlow';
import { ByoEditCodeModal } from 'new/components/flows/types/byo/v2/modals';
import { ByoSourceSetupForm } from 'new/components/flows/types/byo/v2-shells/canvas/ByoSourceSetupForm';
import { UserConfirmationForModals } from 'new/components/UserConfirmationForModals';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type Props = {
  id?: number;
  connectionTypes?: string[];
  onDismiss?: (source: any) => void;
  // To be used as a click callback when ButtonComponent is set
  onButtonClick?: () => void;
  onClose?: () => void;
  flowType?: keyof typeof flowTypes;
  parentDataSetId?: number;
  open: boolean;
  queryParams?: any;
};

const flowTypeComponentMap = {
  genai_rag: AddDataSourceFormContainer,
  streaming: AddDataSourceFormContainer,
  replication: CreateReplicationFlow,
  spark: CreateSparkFlow,
  elt: CreateELTFlow,
  in_memory: CreateDirectFlow,
  custom: CreateDirectFlow,
  cdc: CreateCDCFlow,
  api_server: AddDataSourceFormContainer,
};

const paperStyle = {
  style: {
    background: 'transparent',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    borderRadius: '8px',
    padding: '49px',
  },
};

// BYO (custom) edit modals render a full-bleed white surface (header + body),
// not a centered card on a transparent frame, so they need a solid white Paper.
const byoPaperStyle = {
  style: {
    background: 'var(--color-white, #fff)',
    padding: 0,
  },
};

const ModalSourceForm = (props: Props) => {
  const { connectionTypes, onDismiss, id, open, flowType, parentDataSetId, queryParams } = props;
  // Gates ALL of the v2 (Studio/canvas shell) BYO edit UI, not just its create
  // entry point (FlowSelectionPage.tsx) — without this, a flag-off org still
  // gets routed into ByoEditCodeModal/ByoSourceSetupForm the moment it edits
  // any pre-existing flow_type: 'custom' flow, since flowType==='custom' alone
  // used to be sufficient to take this branch.
  const byoV2Enabled = useFeatureFlag('UI_ENABLE_BYO_V2');

  if (!flowType) {
    console.error('You must provide a flowType to ModalSourceForm.');
    return null;
  }

  const onClose = (event = null, reason = '') => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    if (props.onClose) {
      setTimeout(() => {
        // Why inside a timeout? To keep the animation working
        props.onClose();
      }, 500);
    }
  };

  const onDismissForm = (source: any) => {
    onClose();
    onDismiss?.(source);
  };

  if (flowType === 'custom' && byoV2Enabled) {
    // Code node edit (byoSection='code') → the processor editor (ByoEditCodeModal).
    // Source node edit → the connector wizard (ByoSourceSetupForm) opened in edit
    // mode, mirroring how ModalDestinationForm routes custom → ByoDestinationSetupForm.
    const isCode = queryParams?.byoSection === 'code';
    return (
      <>
        <Dialog
          fullScreen
          keepMounted={false}
          open={open}
          onClose={onClose}
          PaperProps={byoPaperStyle}
          TransitionComponent={Transition}
        >
          {isCode ? (
            <ByoEditCodeModal id={id} onDismiss={onDismissForm} queryParams={queryParams} />
          ) : (
            <ByoSourceSetupForm id={id} isEditMode={id != null} onDismiss={onDismissForm} />
          )}
        </Dialog>
        <UserConfirmationForModals open={open} onConfirm={() => onDismissForm(null)} />
      </>
    );
  }

  const SourceFormComponent = flowTypeComponentMap[flowType];

  return (
    <>
      <Dialog
        fullScreen
        keepMounted={false}
        open={open}
        onClose={onClose}
        PaperProps={paperStyle}
        TransitionComponent={Transition}
      >
        <SourceFormComponent
          isModal
          id={id}
          flowType={flowType}
          // @ts-ignore
          connectionTypes={connectionTypes}
          resourceType="source"
          onDismiss={onDismissForm}
          parentDataSetId={parentDataSetId}
          queryParams={queryParams}
        />
      </Dialog>
      <UserConfirmationForModals open={open} onConfirm={() => onDismissForm(null)} />
    </>
  );
};

export default ModalSourceForm;
